import {
  EddStatus,
  EddSubmission,
  fetchEddReviews,
  fetchKybReviews,
  KybApplication,
  ReviewKind,
  ReviewList,
  TierProgress,
} from "api/admin/kycReviews";
import Table from "components/ui/Table";
import useAdminAccess from "hooks/useAdminAccess";
import { useQuery } from "react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  dateLabel,
  reviewError,
  reviewKey,
  ReviewLayout,
  reviewPath,
  StatusBadge,
  userLabel,
} from "./ReviewComponents";

const eddStatuses: { value: EddStatus; label: string }[] = [
  { value: "submitted", label: "Under review" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Rejected" },
];
const kybStatuses: { value: TierProgress; label: string }[] = [
  { value: "pending", label: "Under review" },
  { value: "completed", label: "Approved" },
  { value: "failed", label: "Rejected" },
  { value: "not_verified", label: "Incomplete" },
];

export default function KycReviewQueue({ kind }: { kind: ReviewKind }) {
  const { admin, can } = useAdminAccess();
  const [params, setParams] = useSearchParams();
  const statuses = kind === "edd" ? eddStatuses : kybStatuses;
  const status =
    statuses.find((entry) => entry.value === params.get("status"))?.value ??
    statuses[0].value;
  const requestedPage = Number(params.get("page") ?? 1);
  const page =
    Number.isSafeInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : 1;
  const query = useQuery<ReviewList<EddSubmission | KybApplication>>(
    [...reviewKey(kind, admin?.id), "list", status, page],
    ({ signal }) =>
      kind === "edd"
        ? fetchEddReviews(page, status as EddStatus, 25, signal)
        : fetchKybReviews(page, status as TierProgress, 25, signal),
    { enabled: can("users.kyc.view"), retry: false },
  );
  const update = (nextStatus: string, nextPage: number) =>
    setParams({ status: nextStatus, page: String(nextPage) });
  const rows = query.data?.results ?? [];
  const meta = query.data?.meta;

  return (
    <ReviewLayout kind={kind}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <label className="block mb-2 text-sm" htmlFor="review-status">
            {kind === "edd" ? "Submission status" : "Application status"}
          </label>
          <select
            id="review-status"
            className="py-2 pl-3 pr-10 text-sm border border-gray-300 rounded"
            value={status}
            onChange={(event) => update(event.target.value, 1)}
          >
            {statuses.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="text-sm underline disabled:opacity-50"
        >
          Refresh reviews
        </button>
      </div>
      {kind === "edd" && (
        <p className="mb-4 text-sm text-gray-500">
          Each row is a submission attempt. Open a customer to review their
          latest submission.
        </p>
      )}
      {kind === "kyb" && (
        <p className="mb-4 text-sm text-gray-500">
          Under review and rejected filters include applications with any
          section in that state. Approved means all three sections are complete.
        </p>
      )}
      <div className="overflow-x-auto">
        <Table
          header={[
            "Customer",
            kind === "edd" ? "Submission" : "Business",
            kind === "edd" ? "Submitted" : "Updated",
            "Status",
            "Review",
          ]}
          isLoading={query.isLoading}
          loadingLabel="Loading KYC reviews"
          errorMessage={query.isError ? reviewError(query.error) : undefined}
          onRetry={() => query.refetch()}
          emptyMessage="No reviews match this status."
          data={rows.map((row) => [
            <div>
              <p className="font-medium">{userLabel(row.user, row.userID)}</p>
              <p className="mt-1 text-xs text-gray-500">
                #{row.userID} · {row.user?.country || "—"}
              </p>
            </div>,
            "reviewStatus" in row
              ? `Attempt ${row.attemptNumber}`
              : row.businessName || "Business details incomplete",
            dateLabel("reviewStatus" in row ? row.submittedAt : row.updatedAt),
            <StatusBadge
              status={
                "reviewStatus" in row ? row.reviewStatus : row.tier4Status
              }
            />,
            <Link
              className="font-medium underline"
              to={`${reviewPath(kind)}/${row.userID}`}
              aria-label={`Review ${userLabel(row.user, row.userID)}`}
            >
              {kind === "edd" ? "Review latest" : "Review application"}
            </Link>,
            undefined,
          ])}
        />
      </div>
      {!query.isError && meta && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-5 text-sm">
          <p>
            {meta.total} {kind === "edd" ? "submissions" : "applications"} ·
            Page {page}
            {meta.totalPages > 0 ? ` of ${meta.totalPages}` : ""}
          </p>
          <div className="flex gap-4">
            <button
              type="button"
              disabled={page === 1 || query.isFetching}
              onClick={() => update(status, page - 1)}
              className="underline disabled:opacity-40"
            >
              Previous page
            </button>
            <button
              type="button"
              disabled={!meta.hasNextPage || query.isFetching}
              onClick={() => update(status, page + 1)}
              className="underline disabled:opacity-40"
            >
              Next page
            </button>
          </div>
        </div>
      )}
    </ReviewLayout>
  );
}
